import React, { useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import countryList from "react-select-country-list";
import Select from "./Select";
import { usePaymentInputs } from "react-payment-inputs";
import { validateCC } from "../utils";
import { emailPattern } from "../constant";
import "../App.css";

function CardInput({ onSubmit, configGateway = {}, onCardNumberBlur }) {
  const options = useMemo(() => countryList().getData(), []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, submitCount },
  } = useForm();

  const { meta, getCardNumberProps, getExpiryDateProps, getCVCProps } =
    usePaymentInputs();

  const cardInfoError = useMemo(() => {
    if (submitCount) {
      if (errors.cardNumber) {
        return meta?.erroredInputs?.cardNumber;
      }

      return meta?.erroredInputs?.expiryDate || meta?.erroredInputs?.cvc;
    }

    return "";
  }, [meta, submitCount, errors]);

  const onSubmitInternal = (data) => {
    if (meta?.erroredInputs?.expiryDate) {
      return;
    }
    data.cardNumber = data.cardNumber.trim().replace(/\s+/g, "", "");
    onSubmit(data);
  };

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmitInternal)} autoComplete="on">
        <label>
          Card holder <span className="required">*</span>
        </label>
        <input
          {...register("cardHolder", {
            required: true,
          })}
          type="text"
          autoComplete="cc-name"
        />
        {errors?.cardHolder?.type === "required" && (
          <p>Card holder is required</p>
        )}
        <label>
          Card information <span className="required">*</span>
        </label>
        <Controller
          name="cardNumber"
          control={control}
          rules={{ required: true, minLength: 12 }}
          defaultValue={""}
          render={({ field }) => (
            <input
              {...field}
              {...getCardNumberProps({
                onChange: (e) => {
                  field.onChange(validateCC(e.target.value));
                },
              })}
              onBlur={(event) => {
                field.onBlur();
                onCardNumberBlur?.(event.target.value.replace(/\s+/g, ""));
              }}
              autoComplete="cc-number"
              value={field.value}
            />
          )}
        />
        <div className="grid-2-columns">
          <Controller
            name="cardExpiryDate"
            control={control}
            rules={{ required: true, minLength: 7 }}
            defaultValue={""}
            render={({ field }) => (
              <input
                {...field}
                {...getExpiryDateProps({
                  onChange: (e) => {
                    field.onChange(e.target.value);
                  },
                })}
                autoComplete="cc-exp"
                value={field.value}
              />
            )}
          />
          <Controller
            name="cardCVC"
            control={control}
            rules={{ required: true, minLength: 3 }}
            defaultValue={""}
            render={({ field }) => (
              <input
                {...field}
                {...getCVCProps({
                  onChange: (e) => {
                    field.onChange(e.target.value);
                  },
                })}
                autoComplete="cc-csc"
                value={field.value}
              />
            )}
          />
        </div>
        {cardInfoError && <p>{cardInfoError}</p>}
        {configGateway?.showPhone && (
          <>
            <label>Phone number</label>
            <input
              {...register("phone")}
              type="tel"
              autoComplete="tel"
              placeholder="Phone number"
            />
          </>
        )}
        {configGateway?.showUnionPayFields && (
          <>
            <label>
              ID type <span className="required">*</span>
            </label>
            <select
              {...register("creditType", { required: true })}
              className="card-input-select"
              defaultValue="IDCARD"
            >
              <option value="IDCARD">IDCARD</option>
              <option value="PASSPORT">PASSPORT</option>
              <option value="OFFICERPASS">OFFICERPASS</option>
              <option value="HM_VISITORPASS">HM_VISITORPASS</option>
              <option value="T_VISITORPASS">T_VISITORPASS</option>
              <option value="OTHER">OTHER</option>
            </select>
            <label>
              ID number <span className="required">*</span>
            </label>
            <input {...register("creditCode", { required: true })} type="text" />
            {errors?.creditCode?.type === "required" && <p>ID number is required</p>}
          </>
        )}
        {configGateway?.zipcodeEnable === 'true' && (
          <>
            <label>Billing address</label>
            <input
              {...register("billingAddress", { required: false })}
              type="text"
              autoComplete="street-address"
            />
            {errors?.billingAddress?.type === "required" && (
              <p>Billing address is required</p>
            )}
            <label>Billing city</label>
            <input
              {...register("billingCity", {
                required: false,
              })}
              type="text"
              autoComplete="address-level2"
            />
            {errors?.billingCity?.type === "required" && (
              <p>Billing city is required</p>
            )}
            <label>Billing state</label>
            <input
              {...register("billingState", {
                minLength: 2,
                maxLength: 2,
              })}
              type="text"
              autoComplete="address-level1"
            />
            {(errors?.billingState?.type === "minLength" ||
              errors?.billingState?.type === "maxLength") && (
                <p>The billing state name should consist of two characters</p>
              )}
            <label>Billing postal</label>
            <input
              {...register("billingPostal", {
                required: false,
              })}
              type="text"
              autoComplete="postal-code"
            />
            {errors?.billingPostal?.type === "required" && (
              <p>Postal code is required</p>
            )}
            <label>Billing country</label>
            <Controller
              name="billingCountry"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={options}
                  placeholder="Select Country"
                  autoComplete="country"
                />
              )}
            />
          </>
        )}
        {configGateway?.requireEmail === 'true' && (
          <>
            <label>Billing email</label>
            <input
              {...register("billingEmail", {
                required: true,
                pattern: emailPattern,
              })}
              type="text"
              autoComplete="email"
            />
            {errors?.billingEmail?.type === "required" && (
              <p>Billing email is required</p>
            )}
            {errors?.billingEmail?.type === "pattern" && (
              <p>Please enter an email address</p>
            )}
          </>
        )}
        {configGateway?.requirePhone === "true" && (
          <>
            <label>Billing phone</label>
            <input
              {...register("billingPhone", {
                required: true,
              })}
              type="tel"
              autoComplete="tel"
            />
            {errors?.billingPhone?.type === "required" && (
              <p>Billing phone is required</p>
            )}
          </>
        )}
        <input type="submit" />
      </form>
    </div>
  );
}

export default CardInput;
